/**
 * Valentine's painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const valentinesPaintingIdeasPack: ToilePack = {
    slug: 'valentines-painting-ideas',
    category: 'craft',
    name: "Valentine's painting ideas",
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'heart', label: 'Petal heart', subject: 'a canvas of a red heart built from overlapping brushstroke petals on white'},
        {key: 'lovebirds', label: 'Lovebirds', subject: 'a canvas of two lovebirds on a blossoming branch, leaning in toward each other'},
        {key: 'balloons', label: 'Heart balloons', subject: 'a canvas of red and pink heart balloons floating up into a pale sky'},
    ],
    styles: [
        {key: 'simple', label: 'Simple strokes', style: 'simple acrylic with bold rounded strokes', palette: 'red, pink and white', framing: 'a small square canvas propped on a white desk, seen straight on'},
        {key: 'dotted', label: 'Dotted', style: 'dotted pointillism in small round dabs of paint', palette: 'crimson, blush and coral on cream', framing: 'a small canvas on a mini easel with scattered rose petals around it'},
        {key: 'thick', label: 'Thick paint', style: 'palette-knife strokes with thick glossy peaks of paint', palette: 'deep red and rose pink', framing: 'a small canvas tied with a red ribbon as a gift on a linen tablecloth'},
    ],
    directions: ['a cupid', 'galaxy heart', 'for kids'],
    directionHint: 'try: a cupid',
    seo: {
        title: "Valentine's Painting Ideas: Heart, Lovebirds, Balloons",
        metaDescription: "Nine small Valentine's canvas ideas to give: a heart of brushstroke petals, two lovebirds and red heart balloons, in three finishes.",
        h1: "Valentine's painting ideas: petal heart, lovebirds, heart balloons",
        tagline: 'Nine small red canvases, three subjects, each in simple bold strokes, dotted paint or thick palette-knife peaks.',
        intro: "Valentine's canvases are small, red and made to give. These nine show three subjects: a red heart built from overlapping brushstroke petals on white, two lovebirds leaning toward each other on a blossoming branch, and red and pink heart balloons floating up into a pale sky. Type another subject, such as a cupid or a galaxy heart, and the same small gift carries it.",
        useCases: [
            'a card-sized gift',
            'a desk canvas for a partner',
            'a classroom Valentine',
            'a small shelf picture for February',
        ],
        faq: [
            {q: 'How big are the canvases?', a: 'The pictures show small square canvases, propped on a desk, on a mini easel or tied with a ribbon.'},
            {q: 'What is the dotted finish?', a: 'Small round dabs of paint in crimson, blush and coral on a cream ground.'},
            {q: 'What are the three settings?', a: 'On a white desk, on a mini easel with rose petals, and tied with red ribbon on a linen tablecloth.'},
            {q: 'Can I change the subject?', a: 'Yes. Type another subject and the small red canvas carries it.'},
        ],
    },
}
